import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog';
import { Button } from '@shared/components/ui/button';
import { Label } from '@shared/components/ui/label';
import { Input } from '@shared/components/ui/input';
import { Textarea } from '@shared/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { Upload, FileText, Loader2 } from 'lucide-react';
import { getPortalToken } from '@shared/services/portalService';

export function UploadDocumentDialog({ open, onOpenChange, projectId, onSuccess }) {
  const [documentType, setDocumentType] = React.useState('rams');
  const [file, setFile] = React.useState(null);
  const [versionNotes, setVersionNotes] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState('');
  const fileInputRef = React.useRef(null);

  const documentTypes = [
    { value: 'rams', label: 'RAMS (Risk Assessment & Method Statement)' },
    { value: 'method_statement', label: 'Method Statement' },
    { value: 'insurance', label: 'Insurance Certificate' },
    { value: 'permit', label: 'Work Permit' },
    { value: 'site_photo', label: 'Site Photo' },
    { value: 'other', label: 'Other' },
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file size (25MB)
      if (selectedFile.size > 25 * 1024 * 1024) {
        setError('File size must be less than 25MB');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const portalToken = getPortalToken();
      if (!portalToken) {
        throw new Error('Not authenticated');
      }

      // Create FormData
      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('document_type', documentType);
      formData.append('file', file);
      if (versionNotes) {
        formData.append('version_notes', versionNotes);
      }

      // Call Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/project-upload-document`,
        {
          method: 'POST',
          headers: {
            'x-portal-token': portalToken,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Upload failed');
      }

      // Reset form
      setFile(null);
      setVersionNotes('');
      setDocumentType('rams');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Call success callback
      if (onSuccess) {
        onSuccess(result.document);
      }

      // Close dialog
      onOpenChange(false);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload RAMS documents, insurance certificates, or other project files.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Document Type */}
          <div className="space-y-2">
            <Label htmlFor="documentType">Document Type</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger>
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent>
                {documentTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="file">File</Label>
            <div 
              onClick={() => fileInputRef.current?.click()}
              className={`
                relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
                ${file 
                  ? 'border-amber-500 bg-amber-50 hover:bg-amber-100' 
                  : 'border-gray-300 bg-gray-50 hover:border-amber-400 hover:bg-amber-50'
                }
                ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <Input
                id="file"
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                disabled={uploading}
                className="hidden"
              />
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <FileText className="h-8 w-8 text-amber-600" />
                  <div className="text-sm font-medium text-amber-900">{file.name}</div>
                  <div className="text-xs text-amber-700">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                  <div className="text-xs text-amber-600 mt-1">
                    Click to change file
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-gray-400" />
                  <div className="text-sm font-medium text-gray-700">
                    Click to select a file
                  </div>
                  <div className="text-xs text-gray-500">
                    PDF, Word, JPG, PNG • Max 25MB
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Version Notes */}
          <div className="space-y-2">
            <Label htmlFor="versionNotes">Version Notes (Optional)</Label>
            <Textarea
              id="versionNotes"
              placeholder="e.g., Updated safety procedures for crane operations"
              value={versionNotes}
              onChange={(e) => setVersionNotes(e.target.value)}
              disabled={uploading}
              rows={3}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
